import React, {
	useEffect,
	useRef,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/pro-thin-svg-icons';
import { Currencies } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import NumericInput from '@client/components/NumericInput';
import { useExchangeRates } from '@client/utils/hooks/useExchangeRates';
import styles from './styles/PaymentExchangeRateInput.module.css';

type Props = {
	value?: number;
	onChange?: (value: number) => void;
	currency: Values<typeof Currencies>;
	fixtureCurrency: Values<typeof Currencies>;
};

const PaymentExchangeRateInput = ({
	value,
	onChange,
	currency,
	fixtureCurrency,
}: Props) => {
	const exchangeRates = useExchangeRates(fixtureCurrency);
	const previousCurrency = useRef(currency);
	const fetchedRate = exchangeRates?.[currency] ?? 1;

	useEffect(() => {
		if (exchangeRates == null || onChange == null) {
			return;
		}

		const currencyChanged = previousCurrency.current !== currency;
		previousCurrency.current = currency;

		if (currency === fixtureCurrency) {
			onChange(1);

			return;
		}

		const rate = exchangeRates[currency as keyof typeof exchangeRates];

		if (rate == null) {
			return;
		}

		if (currencyChanged || value == null) {
			onChange(rate as number);
		}
	}, [
		currency,
		exchangeRates,
		fixtureCurrency,
		onChange,
		value,
	]);

	if (currency === fixtureCurrency) {
		return null;
	}

	return (
		<NumericInput
			value={value}
			min={0}
			precision={6}
			onChange={(nextValue) => onChange?.(nextValue ?? 1)}
			addonAfter={(
				<FontAwesomeIcon
					color="#1677ff"
					className={styles.refreshIcon}
					icon={faRefresh}
					onClick={() => onChange?.(fetchedRate)}
				/>
			)}
		/>
	);
};

export default PaymentExchangeRateInput;
